import React from 'react';
import {FormGroup, Input, Label} from 'reactstrap';
import {AnimationKind, Axis, SceneAnimation} from './types.ts';

interface AnimationEditorProps {
    animation: SceneAnimation | null | undefined;
    onChange: (animation: SceneAnimation | null) => void;
}

// What each kind starts with (the runtime's defaults), and what its speed and
// amount mean. Sway's amount is stored in radians but shown in degrees.
const kinds: Record<
    AnimationKind,
    {
        label: string;
        defaults: SceneAnimation;
        speedLabel: string;
        amountLabel?: string;
        hasAxis: boolean;
    }
> = {
    spin: {
        label: 'Spin',
        defaults: {kind: 'spin', axis: 'y', speed: 0.25, amount: null},
        speedLabel: 'Turns a second (negative turns the other way)',
        hasAxis: true
    },
    bob: {
        label: 'Bob',
        defaults: {kind: 'bob', axis: 'y', speed: 0.5, amount: 0.25},
        speedLabel: 'Cycles a second',
        amountLabel: 'Distance',
        hasAxis: true
    },
    sway: {
        label: 'Sway',
        defaults: {kind: 'sway', axis: 'z', speed: 0.5, amount: 0.26},
        speedLabel: 'Cycles a second',
        amountLabel: 'Degrees each way',
        hasAxis: true
    },
    pulse: {
        label: 'Pulse',
        defaults: {kind: 'pulse', axis: null, speed: 0.5, amount: 0.1},
        speedLabel: 'Cycles a second',
        amountLabel: 'Grows by (a fraction of its size)',
        hasAxis: false
    }
};

const toDegrees = (radians: number) =>
    Math.round(((radians * 180) / Math.PI) * 10) / 10;

const toRadians = (degrees: number) => (degrees * Math.PI) / 180;

const AnimationEditor: React.FC<AnimationEditorProps> = ({
    animation,
    onChange
}) => {
    const kind = animation && kinds[animation.kind] ? animation.kind : null;
    const info = kind ? kinds[kind] : null;

    const handleKindChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value as AnimationKind | '';
        // A new kind starts from its defaults: speeds and amounts mean
        // different things for each.
        onChange(value ? {...kinds[value].defaults} : null);
    };

    const change = (changes: Partial<SceneAnimation>) =>
        animation && onChange({...animation, ...changes});

    const handleSpeedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseFloat(e.target.value);
        change({speed: isNaN(value) ? null : value});
    };

    const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseFloat(e.target.value);
        change({
            amount: isNaN(value)
                ? null
                : kind === 'sway'
                ? toRadians(value)
                : value
        });
    };

    return (
        <div className="p-2">
            <div>
                <b>Animation</b>
            </div>
            <FormGroup>
                <Label>Motion:</Label>
                <Input
                    type="select"
                    value={kind ?? ''}
                    onChange={handleKindChange}
                >
                    <option value="">None</option>
                    {Object.entries(kinds).map(([value, {label}]) => (
                        <option key={value} value={value}>
                            {label}
                        </option>
                    ))}
                </Input>
            </FormGroup>
            {animation && info && (
                <>
                    {info.hasAxis && (
                        <FormGroup>
                            <Label>Axis:</Label>
                            <Input
                                type="select"
                                value={animation.axis ?? info.defaults.axis!}
                                onChange={e =>
                                    change({axis: e.target.value as Axis})
                                }
                            >
                                <option value="x">X</option>
                                <option value="y">Y</option>
                                <option value="z">Z</option>
                            </Input>
                        </FormGroup>
                    )}
                    <FormGroup>
                        <Label>{info.speedLabel}:</Label>
                        <Input
                            type="number"
                            step="0.05"
                            value={animation.speed ?? ''}
                            placeholder={String(info.defaults.speed)}
                            onChange={handleSpeedChange}
                        />
                    </FormGroup>
                    {info.amountLabel && (
                        <FormGroup>
                            <Label>{info.amountLabel}:</Label>
                            <Input
                                type="number"
                                step={kind === 'sway' ? '1' : '0.05'}
                                value={
                                    animation.amount === undefined ||
                                    animation.amount === null
                                        ? ''
                                        : kind === 'sway'
                                        ? toDegrees(animation.amount)
                                        : animation.amount
                                }
                                placeholder={String(
                                    kind === 'sway'
                                        ? toDegrees(info.defaults.amount!)
                                        : info.defaults.amount
                                )}
                                onChange={handleAmountChange}
                            />
                        </FormGroup>
                    )}
                </>
            )}
        </div>
    );
};

export default AnimationEditor;
